// frontend/src/App.jsx
// Root component — handles routing between Login and Dashboard.
// Wraps everything in AuthProvider for global auth state.

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { LanguageProvider } from './utils/i18n';
import { NotificationProvider } from './utils/NotificationContext';
import ScrollToTop from './components/ScrollToTop';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ClientRegister from './pages/ClientRegister';
import Landing from './pages/Landing';
import LandingSalons from './pages/LandingSalons';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import Contact from './pages/Contact';
import CardCustomizer from './pages/CardCustomizer';
import Pricing from './pages/Pricing';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import PastDue from './pages/PastDue';
import AnalyticsPage from './pages/Analytics';
import AdminPanel from './pages/AdminPanel';
import VisitCard from './pages/VisitCard';
import CookieConsent from './components/CookieConsent';
import PostHogAnalytics from './components/PostHogAnalytics';

/**
 * ProtectedRoute — wraps all private routes.
 * Checks in order: session → email confirmed → active subscription.
 * If any check fails, redirects to the appropriate page.
 */
function ProtectedRoute({ children }) {
  const { session, loading, subscriptionStatus, subscriptionEndDate, isEmailConfirmed, isDemo } = useAuth();
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-muted)' }}>Loading…</div>;
  if (isDemo) return children; // Bypass all checks for Demo mode
  if (!session) return <Navigate to="/login" replace />;
  if (!isEmailConfirmed) return <Navigate to="/login" replace />;

  // 1. Check subscription end date
  const endDate = subscriptionEndDate ? new Date(subscriptionEndDate) : null;
  const isExpired = endDate && new Date() >= endDate;

  // 2. Handle specific subscription statuses
  if (subscriptionStatus === 'past_due') {
    return <Navigate to="/past-due" replace />;
  }

  if (subscriptionStatus === 'canceled') {
    // Canceled but still within paid period — allow access
    if (endDate && !isExpired) {
      return children;
    }
    // Canceled and expired (or no end date) — redirect to pricing
    return <Navigate to="/pricing" replace />;
  }

  // 3. Force pricing if expired or not active/trialing
  if (isExpired) {
    return <Navigate to="/pricing" replace />;
  }

  if (subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') {
    return <Navigate to="/pricing" replace />;
  }
  return children;
}

/**
 * AdminRoute — strictly for wafiyo.co@gmail.com
 */
function AdminRoute({ children }) {
  const { session, loading } = useAuth();
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-muted)' }}>Loading…</div>;
  if (session?.user?.email !== 'wafiyo.co@gmail.com') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

/**
 * AuthOnlyRoute — requires login + confirmed email but does NOT check subscription.
 * Used for /pricing so users can select a plan before having an active subscription.
 */
function AuthOnlyRoute({ children }) {
  const { session, loading, isEmailConfirmed, isDemo } = useAuth();
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-muted)' }}>Loading…</div>;
  if (isDemo) return children;
  if (!session) return <Navigate to="/login" replace />;
  if (!isEmailConfirmed) return <Navigate to="/login" replace />;
  return children;
}

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: true, // Fetch fresh data when tab becomes active again
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <LanguageProvider>
          <NotificationProvider>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/salon" element={<LandingSalons />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/confirm-email" element={<ConfirmEmail />} />
                <Route path="/register/:businessId" element={<ClientRegister />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/visit" element={<VisitCard />} />
                <Route path="/pricing" element={<AuthOnlyRoute><Pricing /></AuthOnlyRoute>} />
                <Route path="/past-due" element={<AuthOnlyRoute><PastDue /></AuthOnlyRoute>} />
                <Route
                  path="/dashboard"
                  element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
                />
                <Route
                  path="/dashboard/card-design"
                  element={<ProtectedRoute><CardCustomizer /></ProtectedRoute>}
                />
                <Route
                  path="/dashboard/analytics"
                  element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>}
                />
                <Route
                  path="/admin"
                  element={<AdminRoute><AdminPanel /></AdminRoute>}
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Analytics />
              <PostHogAnalytics />
              <CookieConsent />
            </AuthProvider>
          </NotificationProvider>
        </LanguageProvider>
      </Router>
    </QueryClientProvider>
  );
}
